import { useState } from 'react';

/**
 * A hook that maintains a local state which is reset whenever the external value changes.
 * This is useful for "draft" state that should be overwritten when the source of truth changes (e.g. undo/redo).
 *
 * This implements the "Adjusting state during rendering" pattern recommended by React docs
 * to avoid useEffect for state synchronization.
 *
 * @param externalValue The value from props or external store
 * @returns [localValue, setLocalValue]
 */
export function useBufferedState<T>(externalValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [localValue, setLocalValue] = useState<T>(externalValue);
  const [prevExternalValue, setPrevExternalValue] = useState<T>(externalValue);

  if (externalValue !== prevExternalValue) {
    setPrevExternalValue(externalValue);
    setLocalValue(externalValue);
  }

  return [localValue, setLocalValue];
}
